import { reportAuthTrace } from '@/common/auth-trace.js'
import { isEmbeddedIframe } from '@/embed-base'

export const MP_CLIENT_KEY = 'default'

const H5_LOGIN_KEYS = [
  'token',
  'tokenLogin',
  'expires',
  'uid',
  'ticket',
  'headimgurl',
  'nickname',
  'timestamp',
  'hasPhoneNumber',
  'points',
  'starlight',
]

let syncPromise = null
let syncResolve = null
let syncDone = false
let redeemPromise = null
let lastRedeemedTicket = ''

export function beginMpAuthSync() {
  if (syncDone) return Promise.resolve()
  if (!syncPromise) {
    syncPromise = new Promise((resolve) => {
      syncResolve = resolve
    })
  }
  return syncPromise
}

export function finishMpAuthSync() {
  syncDone = true
  if (syncResolve) {
    syncResolve()
    syncResolve = null
  }
}

export function waitMpAuthReady() {
  if (syncDone) return Promise.resolve()
  return syncPromise || Promise.resolve()
}

export function isSilentLoginPending() {
  return !!window.__MP_SILENT_LOGIN_PENDING__
}

export function isMpGuestSession() {
  return !!window.__MP_GUEST_IN_MINI__
}

export function isInMiniProgram() {
  return !!window.__MP_IN_MINI__
}

export function clearH5LoginStorage(meta = {}) {
  const hadToken = !!(localStorage.getItem('tokenLogin') || localStorage.getItem('token'))
  if (hadToken) {
    reportAuthTrace({
      event: 'CLEAR_SESSION',
      reason_code: meta.reason_code || 'SESSION_INVALID',
      clear_caller: meta.clear_caller || 'clearH5LoginStorage',
      clear_reason: meta.clear_reason || meta.reason_code || 'SESSION_INVALID',
      error_code: meta.error_code,
      api_path: meta.api_path,
      token_source: 'storage',
    })
  }
  H5_LOGIN_KEYS.forEach((key) => localStorage.removeItem(key))
  window.__MP_LOGIN_SUCCESS_AT__ = 0
  window.__MP_LOGIN_REDIRECTING__ = ''
}

function decodeParam(value) {
  if (!value) return ''
  try {
    return decodeURIComponent(String(value))
  } catch (e) {
    return String(value)
  }
}

export function getAuthFromLocation(routeQuery) {
  const query = routeQuery || {}
  const search = new URLSearchParams(window.location.search)
  const pendingTicket = sessionStorage.getItem('MP_PENDING_TICKET') || ''
  const pendingDevice = sessionStorage.getItem('MP_PENDING_DEVICE_ID') || ''
  const ticket = decodeParam(query.ticket || search.get('ticket') || pendingTicket || '')
  const deviceId = decodeParam(query.device_id || search.get('device_id') || pendingDevice || '')
  return { ticket, deviceId }
}

export function stripTicketFromUrl() {
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('ticket') && !url.searchParams.has('device_id')) return
    url.searchParams.delete('ticket')
    url.searchParams.delete('device_id')
    window.history.replaceState(window.history.state, document.title, url.toString())
    // 仅「H5 跳小程序登录再回」才会多压一层同页历史；首次带 ticket 打开不要跳过
    if (sessionStorage.getItem('MP_AWAITING_LOGIN') === '1') {
      sessionStorage.setItem('MP_TICKET_HISTORY_SKIP', '1')
    }
  } catch (e) {
    // ignore
  }
}

function applySilentLoginData(payload, ticket) {
  const { token, expires, expries, device_id, user } = payload || {}
  if (!token) return false
  reportAuthTrace({
    event: 'LOGIN_SUCCESS',
    token,
    device_id: device_id || localStorage.getItem('device_id') || '',
    account_id: user && user.id ? user.id : '',
    token_source: 'login_response',
    reason_code: 'LOGIN_RESPONSE',
    api_path: '/wx/mini_h5_login',
  })
  localStorage.setItem('token', token)
  localStorage.setItem('tokenLogin', token)
  localStorage.setItem('expires', expires || expries || '')
  localStorage.setItem('device_id', device_id || localStorage.getItem('device_id') || '')
  if (ticket) localStorage.setItem('ticket', ticket)
  if (user) {
    if (user.id) localStorage.setItem('uid', user.id)
    if (user.avatar) localStorage.setItem('headimgurl', user.avatar)
    if (user.nickname) localStorage.setItem('nickname', user.nickname)
  }
  reportAuthTrace({
    event: 'WEBVIEW_TOKEN_WRITE',
    reason_code: 'WEBVIEW_OVERWRITE',
    token_source: 'webview',
    token,
    device_id: localStorage.getItem('device_id') || '',
    account_id: user && user.id ? user.id : '',
    api_path: '/wx/mini_h5_login',
  })
  reportAuthTrace({
    event: 'TOKEN_WRITE',
    reason_code: 'LOGIN_RESPONSE',
    token_source: 'storage',
    token,
    device_id: localStorage.getItem('device_id') || '',
    account_id: user && user.id ? user.id : '',
    api_path: '/wx/mini_h5_login',
  })
  window.__MP_LOGIN_SUCCESS_AT__ = Date.now()
  window.__MP_LOGIN_REDIRECTING__ = ''
  window.__MP_GUEST_IN_MINI__ = false
  lastRedeemedTicket = ticket || ''
  return true
}

function isLoginOk(res) {
  const body = res && res.data ? res.data : res
  return body && (body.errcode === 0 || body.errmsg === 'ok')
}

/**
 * @param {object} options
 * @param {boolean} options.inMini
 * @param {string} [options.ticket]
 * @param {string} [options.deviceId]
 * @param {Function} options.postData
 * @param {boolean} [options.allowGuestLogout] 首次进入无 ticket 时清 H5 登录；pageshow 回前台不要清
 * @returns {Promise<{ inMini: boolean, loggedIn: boolean, changed: boolean }>}
 */
export async function syncMiniSession({ inMini, ticket, deviceId, postData, allowGuestLogout = true }) {
  window.__MP_IN_MINI__ = !!inMini

  const nextTicket = ticket || getAuthFromLocation().ticket
  const nextDeviceId = deviceId || getAuthFromLocation().deviceId || localStorage.getItem('device_id') || ''

  if (nextTicket) {
    // 有 ticket 只说明外壳传了登录票；iframe 里仍不是小程序原生 web-view
    window.__MP_IN_MINI__ = isEmbeddedIframe() ? !!inMini : true
    sessionStorage.removeItem('MP_PENDING_TICKET')
    sessionStorage.removeItem('MP_PENDING_DEVICE_ID')
    const result = await redeemTicketOnce({
      ticket: nextTicket,
      deviceId: nextDeviceId,
      postData,
    })
    stripTicketFromUrl()
    if (!result.ok) {
      reportAuthTrace({
        event: 'WEBVIEW_AUTH_ERROR',
        reason_code: 'AUTH_CODE_RECEIVED',
        api_path: '/wx/mini_h5_login',
        token_source: 'webview',
      })
      clearH5LoginStorage({
        clear_caller: 'mp-auth.syncMiniSession:redeemFailed',
        reason_code: 'SESSION_INVALID',
        clear_reason: 'WEBVIEW_REDEEM_FAILED',
        api_path: '/wx/mini_h5_login',
      })
      window.__MP_GUEST_IN_MINI__ = true
      return { inMini: true, loggedIn: false, changed: true }
    }
    return { inMini: true, loggedIn: true, changed: result.changed }
  }

  // 登录回跳丢掉 ticket 页后，旧页可能无 ticket；token 已写入则不要按游客清登录
  if (sessionStorage.getItem('MP_LOGIN_RETURN') === '1') {
    sessionStorage.removeItem('MP_LOGIN_RETURN')
    sessionStorage.removeItem('MP_TICKET_HISTORY_SKIP')
    window.__MP_GUEST_IN_MINI__ = false
    const loggedIn = !!localStorage.getItem('tokenLogin')
    return { inMini: true, loggedIn, changed: loggedIn }
  }

  if (!inMini || !allowGuestLogout) {
    window.__MP_GUEST_IN_MINI__ = false
    return { inMini: !!inMini, loggedIn: !!localStorage.getItem('tokenLogin'), changed: false }
  }

  const hadToken = !!localStorage.getItem('tokenLogin')
  clearH5LoginStorage({
    clear_caller: 'mp-auth.syncMiniSession:guestLogout',
    reason_code: 'TOKEN_MISSING',
    clear_reason: 'WEBVIEW_GUEST_LOGOUT',
  })
  window.__MP_GUEST_IN_MINI__ = true
  return { inMini: true, loggedIn: false, changed: hadToken }
}

export async function redeemTicketOnce({ ticket, deviceId, postData }) {
  if (!ticket) return { ok: false, changed: false }
  if (lastRedeemedTicket === ticket && localStorage.getItem('token')) {
    window.__MP_SILENT_LOGIN_PENDING__ = ''
    return { ok: true, changed: false }
  }
  if (redeemPromise) return redeemPromise

  window.__MP_SILENT_LOGIN_PENDING__ = '1'
  reportAuthTrace({
    event: 'WEBVIEW_TOKEN_INIT',
    reason_code: 'WEBVIEW_INIT',
    token_source: 'webview',
    api_path: '/wx/mini_h5_login',
    device_id: deviceId,
  })
  redeemPromise = (async () => {
    try {
      const res = await postData(
        'postFrontUserLoginSilent',
        {
          ticket,
          device_id: deviceId,
          client_key: MP_CLIENT_KEY,
        },
        {
          withCredentials: true,
          skipAuthRedirect: true,
          skipMpAuthWait: true,
        }
      )
      const body = res && res.data ? res.data : res
      if (isLoginOk(res) && body.data) {
        const ok = applySilentLoginData(body.data, ticket)
        return { ok, changed: ok }
      }
      return { ok: false, changed: false }
    } catch (e) {
      console.error('[syncMiniSession] redeem failed', e)
      return { ok: false, changed: false }
    } finally {
      window.__MP_SILENT_LOGIN_PENDING__ = ''
      redeemPromise = null
    }
  })()

  return redeemPromise
}

export function applyRefreshTokenData(payload) {
  const { token, expires, expries, device_id } = payload || {}
  if (!token) return false
  localStorage.setItem('token', token)
  localStorage.setItem('tokenLogin', token)
  localStorage.setItem('expires', expires || expries || '')
  if (device_id) localStorage.setItem('device_id', device_id)
  reportAuthTrace({
    event: 'TOKEN_WRITE',
    reason_code: 'LOGIN_RESPONSE',
    token_source: 'storage',
    token,
    device_id: device_id || localStorage.getItem('device_id') || '',
    api_path: '/wx/mini_h5_refresh_token',
  })
  return true
}
